import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { BrandLockup } from "../components/Brand";
import { ColorModeToggle } from "../components/ColorModeToggle";
import { SUPPORT_EMAIL } from "../domain/contact";
import { ENTITY, entityAddressLine, entityIdentifiersLine } from "../domain/entity";
import { VersionLine } from "../components/VersionLine";

/** Ordered so the two that create the contract come first, then the schedules
 *  that add to it, then the two extracts that restate parts of it. */
const DOCS = [
  { slug: "terms", title: "Terms of Service",
    d: "The master agreement: what we provide, what you pay, cancellation, liability and governing law. Every other document here is incorporated into it or derived from it." },
  { slug: "privacy", title: "Privacy Policy",
    d: "What personal data we hold, for how long, where it is hosted, and your rights under the Digital Personal Data Protection Act, 2023." },
  { slug: "dpa", title: "Data Processing Addendum",
    d: "You are the Data Fiduciary for your subscribers; we are the Processor. Instructions, retention, breach notification and what we can and cannot erase." },
  { slug: "sla", title: "Service Level Agreement",
    d: "Availability definitions, exclusions and the claim route. No uptime percentage is committed during beta \u2014 and the document says why." },
  { slug: "support", title: "Support Policy",
    d: "How to reach us, severity levels and response targets. Working hours are IST, and there is no 24\u00d77 commitment." },
  { slug: "security", title: "Security Statement",
    d: "Tenancy isolation, identity, transport, workload hardening and backups \u2014 written from measurements, with what we do not claim stated explicitly." },
  { slug: "subprocessors", title: "Sub-processors",
    d: "Every third party that touches the data, named. There are fewer than usual: AnkhOS runs on our own hardware, not a public cloud." },
  { slug: "aup", title: "Acceptable Use Policy",
    d: "What you may not do with the platform, your responsibility for your own subscribers, and what suspension does and does not withdraw." },
  { slug: "payment", title: "Payment Terms",
    d: "Charges, how you pay, when invoices fall due and what late payment costs. An extract of the Terms." },
  { slug: "refunds", title: "Cancellation & Refunds",
    d: "How to cancel, when a refund arises and how to raise a billing error. An extract of the Terms." },
];

export function LegalIndex() {
  const auth = useAuth();
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <BrandLockup to="/welcome" />
          <ColorModeToggle />
          <Button size="small" onClick={() => void auth.signinRedirect()}>
            Sign in
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1.5, fontSize: { xs: 30, md: 40 } }}>
          Policies
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, mb: 4 }}>
          The terms on which AnkhOS is offered to service providers in India.
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          The <strong>Terms of Service</strong> is the master agreement. The Service Level
          Agreement, Data Processing Addendum, Support, Security, Sub-processor and Acceptable Use
          documents are incorporated into it. Payment Terms and Cancellation &amp; Refunds
          reproduce parts of it for convenience — where they differ, the Terms govern.
        </Typography>

        <Stack spacing={2}>
          {DOCS.map((doc) => (
            <Card variant="outlined" key={doc.slug}>
              <CardActionArea component={RouterLink} to={`/legal/${doc.slug}`}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {doc.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {doc.d}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
          {ENTITY.name} ({ENTITY.brand}) is a {ENTITY.form}, {entityIdentifiersLine()}. Registered office:{" "}
          {entityAddressLine().replace(`${ENTITY.name}, `, "")},
          India. Governed by the laws of India;
          courts at Mumbai. For a privacy question, our Grievance Officer is named in the{" "}
          <RouterLink to="/legal/privacy" style={{ color: "inherit" }}>Privacy Policy</RouterLink>.
          {" "}For anything else,{" "}
          <RouterLink to="/contact" style={{ color: "inherit" }}>contact us</RouterLink> or write to{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "inherit" }}>{SUPPORT_EMAIL}</a>.
        </Typography>
      <VersionLine />
      </Container>
    </Box>
  );
}
